/**
 * apps/battle/client/src/mapLayout.ts
 *
 * Hand-authored layout coordinates for the EconQuest SVG node-graph map.
 *
 * LAYOUT RATIONALE
 * ─────────────────
 * The 17 clusters are placed on a normalized (0..1) canvas that maps to a
 * 1200×800 SVG viewBox (scaled responsively).
 *
 * World 1 — Foundation (A–I, left/upper region, x: 0.03–0.48)
 *   The prereq chain is A → B → {C,D} → {E,F} → G → H → I.
 *   We lay this out as a gentle S-curve path from top-left to bottom-center:
 *     Row 1 (y≈0.12): A          — starting node, lone hero departure
 *     Row 2 (y≈0.28): B          — single gateway
 *     Row 3 (y≈0.42): C, D       — first branch (left/right of center)
 *     Row 4 (y≈0.55): E, F       — consequences of C/D; F has 3 prereqs (B,C,D)
 *     Row 5 (y≈0.68): G          — narrows back to single path
 *     Row 6 (y≈0.80): H          — penultimate
 *     Row 7 (y≈0.92): I          — World 1 boss / world-2 gateway
 *
 * World 2 — Advanced (J–Q, right/lower region, x: 0.52–0.97)
 *   The prereq chain is J → K → {L,M} → N → O → P → Q.
 *   Laid out as a mirrored arc from upper-right to lower-center:
 *     Row 1 (y≈0.12): J          — entry (portal from I)
 *     Row 2 (y≈0.25): K          — second node
 *     Row 3 (y≈0.38): L, M       — inflation / monetary fork
 *     Row 4 (y≈0.52): N          — fiscal policy converges L+M+K
 *     Row 5 (y≈0.65): O          — balance of payments
 *     Row 6 (y≈0.78): P          — international trade
 *     Row 7 (y≈0.90): Q          — final boss (portal from P+M)
 *
 * The x-coordinates are chosen so World 1 nodes sit in the left 48% and
 * World 2 nodes in the right 48%, leaving a narrow center corridor for
 * cross-world portal arcs (e.g. A-N, H-K, I-J, I-K).
 *
 * This placement minimises edge crossings and creates a clear "two-act"
 * hero's-journey visual: foundation on the left climbing up, advanced on
 * the right cascading down, meeting at the portal links in the middle.
 */

export interface NodePos {
  x: number; // 0..1  (fraction of SVG viewBox width)
  y: number; // 0..1  (fraction of SVG viewBox height)
}

/** Normalized positions for each cluster (clusterId → {x, y}). */
export const LAYOUT: Record<string, NodePos> = {
  // ── World 1 · Foundation ──────────────────────────────────────────────────
  A: { x: 0.18, y: 0.08 }, // Economic Problem & Opportunity Cost — hero departure
  B: { x: 0.18, y: 0.22 }, // Demand & Supply — single gateway
  C: { x: 0.07, y: 0.37 }, // Elasticity — left branch
  D: { x: 0.29, y: 0.37 }, // Market Failure — right branch
  E: { x: 0.36, y: 0.51 }, // Externalities (← D)
  F: { x: 0.16, y: 0.51 }, // Government Intervention (← B,C,D)
  G: { x: 0.18, y: 0.65 }, // Business Costs & EoS (← F)
  H: { x: 0.18, y: 0.78 }, // Market Structures (← G)
  I: { x: 0.18, y: 0.91 }, // Labour Market (← H) — World 1 climax

  // ── World 2 · Advanced ────────────────────────────────────────────────────
  J: { x: 0.82, y: 0.08 }, // National Income & GDP — World 2 entry
  K: { x: 0.82, y: 0.22 }, // Economic Growth (← J)
  L: { x: 0.70, y: 0.37 }, // Inflation (← K)
  M: { x: 0.92, y: 0.37 }, // Monetary Policy (← K)
  N: { x: 0.82, y: 0.51 }, // Fiscal Policy (← K,L)
  O: { x: 0.82, y: 0.65 }, // Balance of Payments (← N)
  P: { x: 0.70, y: 0.78 }, // International Trade (← O)
  Q: { x: 0.82, y: 0.91 }, // Exchange Rates (← P,M)
};

/** SVG viewBox dimensions. */
export const VIEW_W = 1200;
export const VIEW_H = 860;

/** Node circle radius in SVG units. */
export const NODE_R = 36;

/** Convert a normalized [0..1] position to SVG coordinates. */
export function toSVG(pos: NodePos): { cx: number; cy: number } {
  // Add padding on edges so nodes don't clip
  const PAD_X = NODE_R + 10;
  const PAD_Y = NODE_R + 10;
  return {
    cx: PAD_X + pos.x * (VIEW_W - 2 * PAD_X),
    cy: PAD_Y + pos.y * (VIEW_H - 2 * PAD_Y),
  };
}

// ── Prereq edges (main journey path) ────────────────────────────────────────
/** Directional prereq edges; each entry is [from, to]. */
export const PREREQ_EDGES: [string, string][] = [
  ['A', 'B'],
  ['B', 'C'],
  ['B', 'D'],
  ['D', 'E'],
  ['B', 'F'],
  ['C', 'F'],
  ['D', 'F'],
  ['F', 'G'],
  ['G', 'H'],
  ['H', 'I'],
  ['J', 'K'],
  ['K', 'L'],
  ['K', 'M'],
  ['K', 'N'],
  ['L', 'N'],
  ['N', 'O'],
  ['O', 'P'],
  ['P', 'Q'],
  ['M', 'Q'],
];

// ── Portal tier classification ───────────────────────────────────────────────
/** Portal tier: 6=path, 9=conditional, 12=permanent. */
export type PortalTier = 6 | 9 | 12;

/** Permanent portal pairs (12m — bold violet glow). */
const PERMANENT_PAIRS = new Set(['A-N', 'H-K', 'I-J', 'I-K']);

/** Conditional portal pairs (9m — dashed amber). */
const CONDITIONAL_PAIRS = new Set(['B-L', 'B-J', 'C-Q', 'C-O', 'E-N', 'F-N', 'G-P', 'H-P']);

/** Canonical portal key: always smaller-letter first. */
export function canonicalKey(a: string, b: string): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

/** Determine tier from portalKey (matches PortalState.tier). */
export function portalTierFromKey(key: string): PortalTier {
  if (PERMANENT_PAIRS.has(key)) return 12;
  if (CONDITIONAL_PAIRS.has(key)) return 9;
  return 6;
}

/** CSS colour token for each tier. */
export function tierColor(tier: PortalTier): string {
  switch (tier) {
    case 12: return 'var(--violet)';
    case 9:  return 'var(--warning)';
    case 6:  return 'var(--accent-light)';
  }
}

/** Stroke width for portal arcs by tier. */
export function tierStrokeWidth(tier: PortalTier): number {
  switch (tier) {
    case 12: return 3.5;
    case 9:  return 2.0;
    case 6:  return 1.5;
  }
}

/** Stroke-dasharray for portal arcs (9m is dashed). */
export function tierDashArray(tier: PortalTier): string | undefined {
  return tier === 9 ? '8 5' : undefined;
}

// ── World region bounding boxes for background bands ────────────────────────
export const WORLD1_REGION = { x: 0, y: 0,     w: VIEW_W * 0.50, h: VIEW_H };
export const WORLD2_REGION = { x: VIEW_W * 0.50, y: 0, w: VIEW_W * 0.50, h: VIEW_H };
